import { useMemo } from 'react'
import { useActivityLogs } from '../activity/useActivityLogs'
import { useCheckpoints } from '../checkpoint/useCheckpoints'
import { useChallengeContext } from '../challenge/useChallengeContext'
import { useLeaderboard } from '../team/useLeaderboard'
import { getRouteCoordinates } from '../../logic/routeUtils'
import ActivityLogic from '../../logic/ActivityLogic'
import {
  buildRouteFeatures,
  calculateBounds,
  calculateRouteSegment,
  getProgressPercentage,
} from '../../ui/map/mapUtils'
import type { Feature, GeoJsonProperties, Point } from 'geojson'

type MapPoint = Feature<Point, GeoJsonProperties>
type MapLine = ReturnType<typeof buildRouteFeatures>['yourRoute']

export interface ProgressMapData {
  goal: number
  leaderProgress: number
  yourProgress: number
  paceProgress: number
  yourPoint: MapPoint
  leaderPoint: MapPoint
  pelotonPoint: MapPoint
  yourRoute: MapLine
  remainingRoute: MapLine
  bounds: [[number, number], [number, number]]
}

export function useProgressMapData():
  | { status: 'loading'; error?: Error | null }
  | { status: 'ready'; data: ProgressMapData } {
  const { data: challengeContext } = useChallengeContext()
  const { data: checkpoints, isLoading: checkpointsLoading } = useCheckpoints()
  const { data: leaderboard, isLoading: leaderboardLoading, error } = useLeaderboard()
  const { data: activityLogs, isLoading: logsLoading } = useActivityLogs()

  const routeCoordinates = useMemo(
    () => (checkpoints?.length ? getRouteCoordinates(checkpoints) : []),
    [checkpoints],
  )

  const isLoading =
    leaderboardLoading || checkpointsLoading || logsLoading || !leaderboard || !activityLogs || !challengeContext

  const data = useMemo(() => {
    if (isLoading || routeCoordinates.length < 2) return null

    const goal = challengeContext!.goal
    const leaderProgress = leaderboard!.leaderProgress ?? 0
    const yourProgress = ActivityLogic.getTotalProgress(activityLogs!) ?? 0
    const paceProgress = challengeContext!.challenge.getPaceProgress() ?? 0

    const yourPoint = calculateRouteSegment(
      getProgressPercentage(yourProgress, goal),
      routeCoordinates,
    )
    const leaderPoint = calculateRouteSegment(
      getProgressPercentage(leaderProgress, goal),
      routeCoordinates,
    )
    const pelotonPoint = calculateRouteSegment(
      getProgressPercentage(paceProgress, goal),
      routeCoordinates,
    )
    const { yourRoute, remainingRoute } = buildRouteFeatures(yourPoint, routeCoordinates)
    const bounds = calculateBounds([yourPoint, leaderPoint, pelotonPoint])

    return {
      goal,
      leaderProgress,
      yourProgress,
      paceProgress,
      yourPoint,
      leaderPoint,
      pelotonPoint,
      yourRoute,
      remainingRoute,
      bounds,
    }
  }, [isLoading, routeCoordinates, challengeContext, leaderboard, activityLogs])

  if (isLoading || !data) {
    return { status: 'loading', error: error ?? null }
  }

  return { status: 'ready', data }
}
